import type { CoverFlipType, DisplayMode, FlipDirection } from '@/types/book';

type CoverPageSide = 'left' | 'right';

export type HardCoverTransition = {
  cover: 'front' | 'back';
  motion: 'turn' | 'opening' | 'closing';
  fromPageIndex: number;
  toPageIndex: number;
  fromSide: CoverPageSide;
  toSide: CoverPageSide;
};

export type HardCoverFrame = {
  face: 'from' | 'to';
  scaleX: number;
  perspective: number;
};

export type FlipCalculationPath = 'hard-cover' | 'paper' | 'skip';

type HardCoverTransitionInput = {
  coverFlipType?: CoverFlipType;
  displayMode: DisplayMode;
  currentPageIndex: number;
  pageCount: number;
  direction: FlipDirection;
  flippingPageIndex: number;
  bottomPageIndex: number;
};

const MIN_PROJECTED_SCALE = 0.001;

export function resolveFlipCalculationPath(
  hasHardCoverTransition: boolean,
  paperGeometryValid: boolean
): FlipCalculationPath {
  if (hasHardCoverTransition) return 'hard-cover';
  return paperGeometryValid ? 'paper' : 'skip';
}

export function resolveHardCoverProgress(
  pagePointX: number,
  pageWidth: number,
  reverse: boolean
): number {
  const forwardProgress = Math.max(
    0,
    Math.min(1, (pageWidth - pagePointX) / (2 * pageWidth))
  );
  return reverse ? 1 - forwardProgress : forwardProgress;
}

/** Resolve whether the active transition crosses the front- or back-cover boundary. */
export function resolveHardCoverTransition(
  input: HardCoverTransitionInput
): HardCoverTransition | null {
  if (input.coverFlipType !== 'hard' || input.pageCount < 2) return null;

  if (input.displayMode === 'single') {
    const cover = resolveSingleCover(input);
    if (!cover) return null;

    const coverPageIndex = cover === 'front' ? 0 : input.pageCount - 1;
    const opening =
      (cover === 'front' && input.direction === 'forward') ||
      (cover === 'back' && input.direction === 'back');

    return {
      cover,
      motion: opening ? 'opening' : 'closing',
      fromPageIndex: coverPageIndex,
      toPageIndex: coverPageIndex,
      fromSide: 'right',
      toSide: 'right'
    };
  }

  const cover = resolveDoubleCover(input);
  if (!cover) return null;

  const fromPageIndex =
    input.direction === 'forward' ? input.currentPageIndex + 1 : input.currentPageIndex;
  const toPageIndex = input.flippingPageIndex;
  if (
    fromPageIndex < 0 ||
    fromPageIndex >= input.pageCount ||
    toPageIndex < 0 ||
    toPageIndex >= input.pageCount
  ) {
    return null;
  }

  return {
    cover,
    motion: 'turn',
    fromPageIndex,
    toPageIndex,
    fromSide: input.direction === 'forward' ? 'right' : 'left',
    toSide: input.direction === 'forward' ? 'left' : 'right'
  };
}

function resolveSingleCover(input: HardCoverTransitionInput): 'front' | 'back' | null {
  if (
    (input.direction === 'forward' && input.currentPageIndex === 0) ||
    (input.direction === 'back' && input.flippingPageIndex === 0)
  ) {
    return 'front';
  }

  const hasBackCover = input.pageCount % 2 === 0;
  if (
    hasBackCover &&
    ((input.direction === 'forward' && input.bottomPageIndex === input.pageCount - 1) ||
      (input.direction === 'back' && input.currentPageIndex === input.pageCount - 1))
  ) {
    return 'back';
  }

  return null;
}

function resolveDoubleCover(input: HardCoverTransitionInput): 'front' | 'back' | null {
  if (
    (input.direction === 'forward' && input.currentPageIndex < 0) ||
    (input.direction === 'back' && input.flippingPageIndex === 0)
  ) {
    return 'front';
  }

  const hasBackCover = input.pageCount % 2 === 0;
  if (
    hasBackCover &&
    ((input.direction === 'forward' && input.bottomPageIndex >= input.pageCount - 1) ||
      (input.direction === 'back' && input.currentPageIndex === input.pageCount - 1))
  ) {
    return 'back';
  }

  return null;
}

/** Project a rigid cover rotating zero-to-180 degrees around its spine. */
export function resolveHardCoverFrame(progress: number): HardCoverFrame {
  const clamped = Math.max(0, Math.min(1, progress));
  const scaleX = Math.max(MIN_PROJECTED_SCALE, Math.abs(Math.cos(Math.PI * clamped)));
  const rawPerspective = Math.sin(Math.PI * clamped);

  return {
    face: clamped < 0.5 ? 'from' : 'to',
    scaleX,
    perspective: Math.abs(rawPerspective) < 1e-10 ? 0 : rawPerspective
  };
}

/** Project a single-mode cover through a 90-degree open or close motion. */
export function resolveSingleHardCoverFrame(
  progress: number,
  motion: 'opening' | 'closing'
): HardCoverFrame {
  const clamped = Math.max(0, Math.min(1, progress));
  const angle =
    motion === 'opening' ? (Math.PI / 2) * clamped : (Math.PI / 2) * (1 - clamped);
  const scaleX = Math.max(MIN_PROJECTED_SCALE, Math.cos(angle));
  const rawPerspective = Math.sin(angle);

  return {
    face: 'from',
    scaleX,
    perspective: Math.abs(rawPerspective) < 1e-10 ? 0 : rawPerspective
  };
}
